import React from 'react';
import { Component } from '@/types';
import { TimeTracker } from '@/components/pm/TimeTracker';
import { useQueryTemplateData, isQueryTemplateDataSource } from '@/hooks/useQueryTemplateData';
import { ChartDataSource } from '@/types/chartDataSource';

export interface SharedTimeTrackerComponentProps {
  component: Component;
  isViewerMode?: boolean;
}

const CHART_DATA_SOURCE_TYPES = new Set<string>([
  'static',
  'query-template',
  'timestamp-aggregate',
  'workflow',
  'composite-query',
  'home-assistant-placeholder',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isChartDataSource(value: unknown): value is ChartDataSource {
  return isRecord(value)
    && typeof value.type === 'string'
    && CHART_DATA_SOURCE_TYPES.has(value.type);
}

function getRecordArray(value: unknown): Record<string, unknown>[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const records = value.filter(isRecord);
  return records.length === value.length ? records : undefined;
}

export const SharedTimeTrackerComponent: React.FC<SharedTimeTrackerComponentProps> = ({
  component,
  isViewerMode = false,
}) => {
  const { properties = {} } = component;
  const dataSource = isChartDataSource(properties.dataSource) ? properties.dataSource : undefined;
  const sampleData = getRecordArray(properties.sampleData);
  const componentData = getRecordArray(properties.data);

  // Data source binding (same pattern as chart/kanban/gantt/calendar components)
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);

  const {
    data: queryTemplateData,
    isLoading,
    error,
    refetch,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate,
    initialData: sampleData,
  });

  // Data priority: query template (only when active) > sampleData > component data
  const resolvedData: Record<string, unknown>[] =
    (hasQueryTemplate ? queryTemplateData : null)
    ?? sampleData
    ?? componentData
    ?? [];

  // Loading state
  if (isLoading && !queryTemplateData) {
    return (
      <div
        data-testid="time-tracker-loading"
        className="w-full h-full flex items-center justify-center gap-2 text-muted-foreground text-sm"
      >
        <svg
          className="animate-spin"
          style={{ width: 20, height: 20 }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
        Loading time tracker data...
      </div>
    );
  }

  // Error state
  if (error && !queryTemplateData) {
    return (
      <div
        data-testid="time-tracker-error"
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span className="text-sm text-destructive">{error}</span>
        <button
          onClick={() => refetch()}
          style={{
            fontSize: 12,
            color: '#3b82f6',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <TimeTracker
      component={component}
      data={resolvedData}
      isViewerMode={isViewerMode}
    />
  );
};

SharedTimeTrackerComponent.displayName = 'SharedTimeTrackerComponent';
